import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { studentsAPI, usersAPI } from '../services/api';
import { useOpenParam } from '../utils/openParam';
import { useAuth } from '../contexts/AuthContext';
import { FiPlus, FiEdit, FiTrash2, FiSearch, FiChevronUp, FiChevronDown, FiList, FiColumns, FiCheck } from 'react-icons/fi';
import StudentModal from './StudentModal';
import './Students.css';

const PIPELINE_STATUSES = ['NEW', 'CONTACTED', 'INTERESTED', 'APPLIED', 'ENROLLED', 'INACTIVE', 'REJECTED'];

// Returns hours elapsed since a date string
const hoursSince = (dateStr) => {
  if (!dateStr) return null;
  return (Date.now() - new Date(dateStr).getTime()) / 3_600_000;
};

// SLA badge for NEW/CONTACTED students: green <2h, orange 2-24h, red >24h
const SLABadge = ({ student }) => {
  if (!['NEW', 'CONTACTED'].includes(student.status)) return null;
  const hours = hoursSince(student.created_at);
  if (hours === null) return null;
  const color = hours < 2 ? '#43e97b' : hours < 24 ? '#f5a623' : '#f5576c';
  const label = hours < 1
    ? `${Math.round(hours * 60)}m`
    : hours < 24
    ? `${Math.round(hours)}h`
    : `${Math.round(hours / 24)}d`;
  return (
    <span
      title="Time since lead created"
      style={{
        display: 'inline-block',
        background: color + '22',
        color,
        border: `1px solid ${color}`,
        borderRadius: '10px',
        padding: '1px 7px',
        fontSize: '11px',
        fontWeight: 600,
        marginLeft: '4px',
      }}
    >
      {label}
    </span>
  );
};

const Students = () => {
  const { isAdmin } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [applicationTypeFilter, setApplicationTypeFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [orderBy, setOrderBy] = useState('-created_at');
  const [showModal, setShowModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [agents, setAgents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'pipeline'

  const [listError, setListError] = useState(null);

  useEffect(() => {
    fetchStudents();
    if (isAdmin()) {
      fetchAgentsAndEmployees();
    }
  }, [page, statusFilter, yearFilter, applicationTypeFilter, paymentStatusFilter, orderBy, isAdmin]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      setListError(null);
      const params = {
        page,
        search: searchTerm || undefined,
        status: statusFilter || undefined,
        year: yearFilter || undefined,
        application_type: applicationTypeFilter || undefined,
        payment_status: paymentStatusFilter || undefined,
        ordering: orderBy || undefined,
      };
      const response = await studentsAPI.list(params);
      setStudents(response.data.results || response.data);
      if (response.data.count) {
        setTotalPages(Math.ceil(response.data.count / 50));
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      setListError('Failed to load students. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAgentsAndEmployees = async () => {
    try {
      const [agentsResponse, employeesResponse] = await Promise.all([
        usersAPI.agents(),
        usersAPI.permanentEmployees(),
      ]);
      
      const agentsData = agentsResponse.data || [];
      const employeesData = employeesResponse.data || [];
      
      setAgents(agentsData);
      setEmployees(employeesData);
      
      // Combine agents and employees for assignment dropdown
      const combined = [
        ...agentsData.map(agent => ({ ...agent, displayName: `${agent.username} (Agent)` })),
        ...employeesData.map(emp => ({ ...emp, displayName: `${emp.username} (Employee)` }))
      ];
      setAssignableUsers(combined);
    } catch (error) {
      console.error('Error fetching agents and employees:', error);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (page === 1) {
        fetchStudents();
      } else {
        setPage(1);
      }
    }, 500);

    return () => clearTimeout(debounce);
  }, [searchTerm]);

  useEffect(() => {
    if (!listError) return;
    const t = setTimeout(() => setListError(null), 6000);
    return () => clearTimeout(t);
  }, [listError]);

  const toggleSort = (field) => {
    const isDesc = orderBy === `-${field}`;
    setOrderBy(isDesc ? field : `-${field}`);
    setPage(1);
  };

  const SortableTh = ({ field, label }) => {
    const isActive = orderBy === field || orderBy === `-${field}`;
    const isAsc = orderBy === field;
    return (
      <th
        className={isActive ? 'sortable-th active' : 'sortable-th'}
        onClick={() => toggleSort(field)}
        title={`Sort by ${label}`}
      >
        {label}
        {isActive ? (
          isAsc ? <FiChevronUp className="sort-icon" /> : <FiChevronDown className="sort-icon" />
        ) : (
          <span className="sort-placeholder" />
        )}
      </th>
    );
  };

  const handleCreate = () => {
    setSelectedStudent(null);
    setShowModal(true);
  };

  const handleEdit = (student) => {
    setSelectedStudent(student);
    setShowModal(true);
  };
  useOpenParam('student', (id) => studentsAPI.get(id).then((r) => handleEdit(r.data)).catch(() => {}));

  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const handleDeleteRequest = (id) => {
    setDeleteConfirmId(id);
    setDeleteError(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await studentsAPI.delete(deleteConfirmId);
      setDeleteConfirmId(null);
      setDeleteLoading(false);
      // Remove from local list immediately for snappiness, then re-fetch
      setStudents(prev => prev.filter(s => s.id !== deleteConfirmId));
      fetchStudents();
    } catch (error) {
      setDeleteLoading(false);
      setDeleteError(
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        'Failed to delete student. Please try again.'
      );
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmId(null);
    setDeleteError(null);
    setDeleteLoading(false);
  };

  // Keep old name for the inline button handler
  const handleDelete = handleDeleteRequest;

  const handleStatusChange = async (id, newStatus) => {
    try {
      await studentsAPI.updateStatus(id, newStatus);
      setListError(null);
      fetchStudents();
    } catch (error) {
      setListError('Failed to update student status. Please try again.');
    }
  };

  const handleMarkCommissionPaid = async (student) => {
    const amount = parseFloat(student.commission_amount || 0).toFixed(2);
    if (!window.confirm(`Mark the $${amount} agent commission for ${student.full_name} as paid? This cannot be undone from the UI.`)) return;
    try {
      await studentsAPI.markCommissionPaid(student.id);
      setListError(null);
      fetchStudents();
    } catch (error) {
      setListError(error?.response?.data?.detail || 'Failed to mark commission as paid. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      NEW: '#011ff3',
      CONTACTED: '#f093fb',
      INTERESTED: '#4facfe',
      APPLIED: '#43e97b',
      ENROLLED: '#38f9d7',
      REJECTED: '#f5576c',
      INACTIVE: '#999',
    };
    return colors[status] || '#666';
  };

  // Pipeline view: group students by status
  const renderPipeline = () => {
    const grouped = {};
    PIPELINE_STATUSES.forEach((s) => (grouped[s] = []));
    students.forEach((s) => {
      if (grouped[s.status] !== undefined) grouped[s.status].push(s);
    });

    return (
      <div
        style={{
          display: 'flex',
          gap: '12px',
          overflowX: 'auto',
          padding: '8px 0 16px',
          alignItems: 'flex-start',
        }}
      >
        {PIPELINE_STATUSES.map((status) => (
          <div
            key={status}
            style={{
              minWidth: '220px',
              flex: '0 0 220px',
              background: 'var(--card-bg, #1e1e2e)',
              borderRadius: '10px',
              padding: '12px',
              border: `2px solid ${getStatusColor(status)}33`,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '10px',
              }}
            >
              <span
                style={{
                  fontWeight: 700,
                  fontSize: '13px',
                  color: getStatusColor(status),
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {status}
              </span>
              <span
                style={{
                  background: getStatusColor(status) + '33',
                  color: getStatusColor(status),
                  borderRadius: '50%',
                  width: '22px',
                  height: '22px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: 700,
                }}
              >
                {grouped[status].length}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {grouped[status].length === 0 ? (
                <div style={{ color: '#666', fontSize: '12px', textAlign: 'center', padding: '12px 0' }}>
                  No students
                </div>
              ) : (
                grouped[status].map((student) => (
                  <div
                    key={student.id}
                    style={{
                      background: 'var(--bg-secondary, #2a2a3e)',
                      borderRadius: '8px',
                      padding: '10px',
                      fontSize: '13px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <strong style={{ fontSize: '13px' }}>
                        {student.full_name || `${student.first_name} ${student.last_name}`}
                      </strong>
                      <span style={{ display: 'flex', gap: '3px' }}>
                        <SLABadge student={student} />
                      </span>
                    </div>
                    <div style={{ color: '#888', fontSize: '12px', marginBottom: '2px' }}>
                      {student.phone || student.email}
                    </div>
                    {student.assigned_agent_detail?.username && (
                      <div style={{ color: '#666', fontSize: '11px', marginBottom: '6px' }}>
                        Agent: {student.assigned_agent_detail.username}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                      <button
                        className="btn-icon"
                        title="Edit student"
                        onClick={() => handleEdit(student)}
                        style={{ flex: 1, fontSize: '12px', padding: '4px 0' }}
                      >
                        <FiEdit style={{ marginRight: '3px' }} /> Edit
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="students-page">
      <div className="page-header">
        <div className="header-actions-left">
          <button className="btn-primary" onClick={handleCreate}>
            <FiPlus /> Add Student
          </button>
          <div style={{ display: 'flex', marginLeft: '10px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #333' }}>
            <button
              title="Table view"
              onClick={() => setViewMode('table')}
              style={{
                padding: '6px 12px',
                background: viewMode === 'table' ? '#011ff3' : 'transparent',
                color: viewMode === 'table' ? '#fff' : '#888',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '13px',
              }}
            >
              <FiList size={14} /> Table
            </button>
            <button
              title="Pipeline view"
              onClick={() => setViewMode('pipeline')}
              style={{
                padding: '6px 12px',
                background: viewMode === 'pipeline' ? '#011ff3' : 'transparent',
                color: viewMode === 'pipeline' ? '#fff' : '#888',
                border: 'none',
                borderLeft: '1px solid #333',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '13px',
              }}
            >
              <FiColumns size={14} /> Pipeline
            </button>
          </div>
        </div>
        <div className="header-actions-right">
          <div className="search-box">
            <FiSearch />
            <input
              type="text"
              placeholder="Search students..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Statuses</option>
            <option value="NEW">New</option>
            <option value="CONTACTED">Contacted</option>
            <option value="INTERESTED">Interested</option>
            <option value="APPLIED">Applied</option>
            <option value="ENROLLED">Enrolled</option>
            <option value="REJECTED">Rejected</option>
            <option value="INACTIVE">Inactive</option>
          </select>
          <select
            className="filter-select"
            value={yearFilter}
            onChange={(e) => { setYearFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Years</option>
            <option value="2025">2025</option>
            <option value="2026">2026</option>
            <option value="2027">2027</option>
          </select>
          <select
            className="filter-select"
            value={applicationTypeFilter}
            onChange={(e) => { setApplicationTypeFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Types</option>
            <option value="SCHOLARSHIP">Scholarship</option>
            <option value="SELF_FUNDED">Self-Funded</option>
            <option value="TURKISH_LANGUAGE_VISA">Language</option>
          </select>
          <select
            className="filter-select"
            value={paymentStatusFilter}
            onChange={(e) => { setPaymentStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Payments</option>
            <option value="NOT_PAID">Not Paid</option>
            <option value="PARTIAL">Partial</option>
            <option value="PAID">Paid</option>
            <option value="REFUNDED">Refunded</option>
          </select>
        </div>
      </div>

      {listError && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 16px', margin: '0 0 14px',
          background: 'rgba(245,87,108,0.12)', border: '1px solid rgba(245,87,108,0.3)',
          borderRadius: 8, color: '#f5576c', fontSize: 13,
        }}>
          {listError}
        </div>
      )}

      {loading ? (
        <div className="loading">Loading students...</div>
      ) : viewMode === 'pipeline' ? (
        renderPipeline()
      ) : (
        <>
          <div className="students-table-container">
            <table className="students-table">
              <thead>
                <tr>
                  <SortableTh field="student_id" label="Student ID" />
                  <SortableTh field="first_name" label="Name" />
                  <SortableTh field="email" label="Email" />
                  <SortableTh field="phone" label="Phone" />
                  <SortableTh field="status" label="Status" />
                  <th>Agent</th>
                  <th>Payment</th>
                  {isAdmin() && <th>Commission</th>}
                  <SortableTh field="created_at" label="Created" />
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {students.length === 0 ? (
                  <tr>
                    <td colSpan={isAdmin() ? 10 : 9} className="empty-state">
                      No students found
                    </td>
                  </tr>
                ) : (
                  students.map((student) => (
                    <tr key={student.id}>
                      <td>
                        <span className="student-id-badge">{student.student_id || `ID-${student.id}`}</span>
                      </td>
                      <td>
                        <strong>{student.full_name || `${student.first_name} ${student.last_name}`}</strong>
                      </td>
                      <td>{student.email}</td>
                      <td>{student.phone || '-'}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <select
                            className="status-select"
                            value={student.status}
                            onChange={(e) => handleStatusChange(student.id, e.target.value)}
                            style={{ borderColor: getStatusColor(student.status) }}
                          >
                            <option value="NEW">New</option>
                            <option value="CONTACTED">Contacted</option>
                            <option value="INTERESTED">Interested</option>
                            <option value="APPLIED">Applied</option>
                            <option value="ENROLLED">Enrolled</option>
                            <option value="REJECTED">Rejected</option>
                            <option value="INACTIVE">Inactive</option>
                          </select>
                          <SLABadge student={student} />
                        </div>
                      </td>
                      <td>{student.assigned_agent_name || student.assigned_agent_detail?.username || '-'}</td>
                      <td>
                        <span className="payment-badge">
                          {student.payment_status === 'PAID'
                            ? 'Paid'
                            : student.payment_status === 'PARTIAL'
                            ? 'Partial'
                            : student.payment_status === 'REFUNDED'
                            ? 'Refunded'
                            : 'Not Paid'}
                        </span>
                      </td>
                      {isAdmin() && (
                        <td>
                          ${parseFloat(student.commission_amount || 0).toFixed(2)}
                          {parseFloat(student.commission_amount || 0) > 0 && (
                            <div style={{ marginTop: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
                              <span className={`commission-badge ${student.commission_paid ? 'paid' : 'unpaid'}`}>
                                {student.commission_paid ? 'Paid' : 'Unpaid'}
                              </span>
                              {!student.commission_paid && (
                                <button
                                  className="btn-icon"
                                  onClick={() => handleMarkCommissionPaid(student)}
                                  title="Mark agent commission as paid"
                                >
                                  <FiCheck />
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      )}
                      <td>{new Date(student.created_at).toLocaleDateString()}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-icon"
                            onClick={() => handleEdit(student)}
                            title="Edit"
                          >
                            <FiEdit />
                          </button>
                          <button
                            className="btn-icon btn-danger"
                            onClick={() => handleDelete(student.id)}
                            title="Delete"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </button>
              <span>Page {page} of {totalPages}</span>
              <button
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {showModal && createPortal(
        <StudentModal
          student={selectedStudent}
          agents={assignableUsers || []}
          onClose={() => {
            setShowModal(false);
            setSelectedStudent(null);
          }}
          onSave={() => {
            fetchStudents();
            setShowModal(false);
            setSelectedStudent(null);
          }}
        />,
        document.body
      )}

      {/* Delete confirmation modal */}
      {deleteConfirmId && createPortal(
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'rgba(0,0,0,0.72)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#13131f', border: '1px solid #2a2a3d',
            borderRadius: 14, padding: '32px', width: 420, maxWidth: '92vw',
            textAlign: 'center',
          }}>
            <div style={{
              width: 52, height: 52, borderRadius: '50%',
              background: 'rgba(245,87,108,0.12)', border: '2px solid #f5576c',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px',
            }}>
              <FiTrash2 size={22} color="#f5576c" />
            </div>
            <h3 style={{ color: '#fff', margin: '0 0 10px', fontSize: 18 }}>Delete student?</h3>
            <p style={{ color: '#888', fontSize: 13, margin: '0 0 24px', lineHeight: 1.6 }}>
              This will soft-delete the record. You can recover it later from the{' '}
              <strong style={{ color: '#aaa' }}>Deleted Students</strong> page.
            </p>
            {deleteError && (
              <div style={{
                marginBottom: 16, background: 'rgba(245,87,108,0.08)',
                border: '1px solid rgba(245,87,108,0.25)', borderRadius: 8,
                padding: '10px 14px', color: '#f5576c', fontSize: 13,
              }}>
                {deleteError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={handleDeleteCancel}
                disabled={deleteLoading}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 8,
                  border: '1px solid #2a2a3d', background: 'transparent',
                  color: '#aaa', cursor: 'pointer', fontSize: 14,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 8, border: 'none',
                  background: deleteLoading ? '#5a1a1a' : 'linear-gradient(135deg,#f5576c,#f093fb)',
                  color: '#fff', fontWeight: 700,
                  cursor: deleteLoading ? 'not-allowed' : 'pointer', fontSize: 14,
                }}
              >
                {deleteLoading ? 'Deleting…' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Students;

